import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/components/trans';
import {AdminOnboardingResources} from '../../../../../libs/shared/core/src/models/api/companyPreferencesModel';
import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {makeStrictEnumRuntimeType} from '../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {BoxedIcon} from '../../../../../libs/shared/ui/src/components/icon/boxedIcon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {RuleBundleModel} from '../../../../models/api/ruleBundleModel';
import {TagModel} from '../../../../models/api/tagModel';
import {AppState} from '../../../../store/appState';
import {getCircles} from '../../../../store/data/dataSelectors';
import {getInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {OnboardingGuideSlide, OnboardingGuideSlideProps} from '../../guide/onboardingGuideSlide';
import {
  StyledColumnFormDescriptionDiv,
  StyledOnboardingFormTitleDiv,
  StyledOnboardingFormTitlesDiv,
} from '../onboardingWelcomeHelpers';

/*
 * Props.
 */

export interface OnboardingWorkflowsCreatedSlideProps
  extends Pick<OnboardingGuideSlideProps, 'navPrimary' | 'navSecondary'> {
  companyName: string;
  adminOnboardingResources: AdminOnboardingResources;
  shouldShowViewCreatedResources: boolean;
  team: Pick<TeamModel, 'name' | 'color'>;
  inbox: Pick<InboxModel, 'name' | 'color'>;

  tags: ReadonlyArray<TagModel>;
  rules: ReadonlyArray<RuleBundleModel>;

  onNavigateToRules?: (namespace: string) => void;
  onNavigateToSharedInbox?: (namespace: string, inboxId: number) => void;
  onNavigateToCircle?: (circleId: number) => void;
}

export enum WorkflowGoal {
  ENSURE_TIMELY_RESPONSE_TO_CUSTOMERS = 'ensure_timely_response_to_customers',
  UNDERSTAND_CONVERSATION_TRENDS = 'understand_conversation_trends',
  GIVE_EACH_CONVERSATION_A_CLEAR_OWNER = 'give_each_conversation_a_clear_owner',
}

export const RWorkflowGoal = makeStrictEnumRuntimeType(WorkflowGoal);

const goalNames: Record<WorkflowGoal, string> = {
  [WorkflowGoal.ENSURE_TIMELY_RESPONSE_TO_CUSTOMERS]: 'Set reply time goal',
  [WorkflowGoal.UNDERSTAND_CONVERSATION_TRENDS]: 'Tag',
  [WorkflowGoal.GIVE_EACH_CONVERSATION_A_CLEAR_OWNER]: 'Assign to teammates',
};

const orderedGoals = [
  WorkflowGoal.GIVE_EACH_CONVERSATION_A_CLEAR_OWNER,
  WorkflowGoal.ENSURE_TIMELY_RESPONSE_TO_CUSTOMERS,
  WorkflowGoal.UNDERSTAND_CONVERSATION_TRENDS,
];

/*
 * Styles.
 */

const StyledHalfContainer = styled.div`
  flex: 0 0 50%;
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledLineWrapperV3 = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
`;

const StyledResourceWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 16px;
  justify-content: space-between;
`;

const StyledLineOne = styled.div`
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.large};
  line-height: 19px;
`;

const StyledLineTwo = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  font-size: ${fontSizes.medium};
`;

const StyledResourcesCreatedLabel = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.verySmall};
  line-height: 16px;
`;

/*
 * Component.
 */

export const OnboardingWorkflowsCreatedSlide: FC<OnboardingWorkflowsCreatedSlideProps> = ({
  companyName,
  adminOnboardingResources,
  shouldShowViewCreatedResources,
  team,
  inbox,
  rules,
  tags,
  onNavigateToRules,
  onNavigateToSharedInbox,
  onNavigateToCircle,
  ...slideProps
}) => {
  const {circleId, inboxId, goals} = adminOnboardingResources;
  const circleName = useAppSelector((state: AppState) =>
    circleId ? getCircles(state)[circleId]?.name : undefined,
  );
  const inboxName = useAppSelector((state: AppState) =>
    inboxId ? getInboxes(state)[inboxId]?.name : undefined,
  );

  const getTitleMessage = () => {
    const hasCircle = Boolean(circleId);
    const hasInbox = Boolean(inboxId);
    const hasGoals = (goals?.length ?? 0) > 0;

    if (hasCircle && hasInbox && hasGoals) {
      return (
        <Trans id="onboardingwelcome-workflows-created-title">Your inbox and rules are set up! 🎉</Trans>
      );
    }

    if (!hasCircle && !hasInbox && hasGoals) {
      return (
        <Trans id="onboardingwelcome-workflows-created-title-rules-only">Your rules are set up! 🎉</Trans>
      );
    }

    return <Trans id="onboardingwelcome-workflows-created-title-inbox-only">Your inbox is set up! 🎉</Trans>;
  };

  return (
    <OnboardingGuideSlide {...slideProps} isLeftContent>
      <StyledHalfContainer>
        <StyledOnboardingFormTitlesDiv>
          <StyledOnboardingFormTitleDiv>{getTitleMessage()}</StyledOnboardingFormTitleDiv>
        </StyledOnboardingFormTitlesDiv>
        <StyledColumnFormDescriptionDiv>
          <Trans id="onboardingwelcome-workflows-created-edit-later-v3">
            You can always refine these in your settings
          </Trans>
        </StyledColumnFormDescriptionDiv>
        {renderContentV3(
          {...adminOnboardingResources, circleName, inboxName},
          shouldShowViewCreatedResources,
          onNavigateToRules,
          onNavigateToSharedInbox,
          onNavigateToCircle,
        )}
      </StyledHalfContainer>
    </OnboardingGuideSlide>
  );
};

interface AdminOnboardingDetailedResources extends AdminOnboardingResources {
  circleName: string | undefined;
  inboxName: string | undefined;
}

function renderContentV3(
  adminOnboardingResources: AdminOnboardingDetailedResources,
  shouldShowViewCreatedResources: boolean,
  onNavigateToRules?: (namespace: string) => void,
  onNavigateToSharedInbox?: (namespace: string, inboxId: number) => void,
  onNavigateToCircle?: (circleId: number) => void,
) {
  const {circleId, circleName, inboxId, inboxName, namespace, goals} = adminOnboardingResources;
  const sortedGoals = goals?.filter(RWorkflowGoal.is) ?? [];
  sortedGoals.sort((a, b) => orderedGoals.indexOf(a) - orderedGoals.indexOf(b));

  return (
    <StyledContent>
      <StyledResourcesCreatedLabel>
        <Trans id="onboarding-welcome-resources-created">Resources created</Trans>
      </StyledResourcesCreatedLabel>

      {circleId && (
        <StyledLineWrapperV3>
          <StyledResourceWrapper>
            <BoxedIcon name="group" />
            <StyledDetailsWrapper>
              <StyledLineOne>
                <Trans id="onboarding-welcome-created-teammate-group">Teammate group</Trans>
              </StyledLineOne>
              <StyledLineTwo>{circleName}</StyledLineTwo>
            </StyledDetailsWrapper>
          </StyledResourceWrapper>
          {shouldShowViewCreatedResources && onNavigateToCircle && (
            <Button
              variant={ButtonVariantsEnum.SECONDARY}
              size={VisualSizesEnum.SMALL}
              onClick={() => onNavigateToCircle(circleId)}
            >
              <Trans id="onboarding-welcome-created-teammate-group-cta">View</Trans>
            </Button>
          )}
        </StyledLineWrapperV3>
      )}

      {inboxId && namespace && (
        <StyledLineWrapperV3>
          <StyledResourceWrapper>
            <BoxedIcon name="inboxFilled" />
            <StyledDetailsWrapper>
              <StyledLineOne>
                <Trans id="onboarding-welcome-created-shared-inbox">Shared inbox</Trans>
              </StyledLineOne>
              <StyledLineTwo>{inboxName}</StyledLineTwo>
            </StyledDetailsWrapper>
          </StyledResourceWrapper>
          {shouldShowViewCreatedResources && onNavigateToSharedInbox && (
            <Button
              variant={ButtonVariantsEnum.SECONDARY}
              size={VisualSizesEnum.SMALL}
              onClick={() => onNavigateToSharedInbox(namespace, inboxId)}
            >
              <Trans id="onboarding-welcome-created-shared-inbox-cta">View</Trans>
            </Button>
          )}
        </StyledLineWrapperV3>
      )}

      {sortedGoals.length > 0 && namespace && (
        <StyledLineWrapperV3>
          <StyledResourceWrapper>
            <BoxedIcon name="gearFilled" />
            <StyledDetailsWrapper>
              <StyledLineOne>
                <Trans id="onboarding-welcome-created-rules">Rules</Trans>
              </StyledLineOne>
              <StyledLineTwo>{sortedGoals.map((goal) => goalNames[goal]).join('; ')}</StyledLineTwo>
            </StyledDetailsWrapper>
          </StyledResourceWrapper>
          {shouldShowViewCreatedResources && onNavigateToRules && (
            <Button
              variant={ButtonVariantsEnum.SECONDARY}
              size={VisualSizesEnum.SMALL}
              onClick={() => onNavigateToRules(namespace)}
            >
              <Trans id="onboarding-welcome-created-rules-cta">View</Trans>
            </Button>
          )}
        </StyledLineWrapperV3>
      )}
    </StyledContent>
  );
}
